import { readFederatedCurrentUser } from '@atlas-sp/aga-common-lib/current-user';

import type { CurrentUserContextType as HostCurrentUserContextType } from '@/providers/CurrentUserProvider/CurrentUserContext';

export function toHostNavigationCurrentUser(
  federated: ReturnType<typeof readFederatedCurrentUser>,
): HostCurrentUserContextType | undefined {
  if (!federated?.user && !federated?.permissionStrings?.length) {
    return undefined;
  }

  const hostExtras = federated as unknown as HostCurrentUserContextType | undefined;

  return {
    user: federated.user as unknown as HostCurrentUserContextType['user'],
    permissionStrings: federated.permissionStrings,
    userRolesByApp: hostExtras?.userRolesByApp,
    facilitiesByBusiness: hostExtras?.facilitiesByBusiness,
  };
}

function authorizationScore(user: HostCurrentUserContextType | undefined): number {
  if (!user) {
    return 0;
  }
  return user.permissionStrings?.length ?? (user.user ? 1 : 0);
}

/**
 * Picks the richest authorization snapshot for federated remotes.
 * Prevents empty navigation contexts from downgrading permissions mid-render.
 */
export function resolveNavigationCurrentUser(
  reactUser: HostCurrentUserContextType | undefined,
): HostCurrentUserContextType | undefined {
  const federatedUser = toHostNavigationCurrentUser(readFederatedCurrentUser());
  const windowUser =
    typeof window !== 'undefined'
      ? (window.__federatedNavigationContext?.currentUser as HostCurrentUserContextType | undefined)
      : undefined;

  const candidates = [reactUser, federatedUser, windowUser].filter(Boolean) as HostCurrentUserContextType[];
  if (candidates.length === 0) {
    return undefined;
  }

  return candidates.reduce((best, candidate) =>
    authorizationScore(candidate) > authorizationScore(best) ? candidate : best,
  );
}

/**
 * Common-lib AppProvider NavigationProvider overwrites `window.__federatedNavigationContext`
 * without `currentUser`. Restore authorization for aga-components `WithPermission`.
 */
export function restoreFederatedNavigationUserOnWindow(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const resolved = resolveNavigationCurrentUser(
    window.__federatedNavigationContext?.currentUser as HostCurrentUserContextType | undefined,
  );
  if (!resolved) {
    return;
  }

  const navigationContext = window.__federatedNavigationContext ?? {};
  window.__federatedNavigationContext = {
    ...navigationContext,
    currentUser: resolved,
  };
}
